<template>
    <v-dialog v-model="dialog" max-width="600px">
        <template v-slot:activator="{ on: dialog }">
            <slot name="activator" :dialog="dialog">
                <v-tooltip top>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn
                            v-on="{...tooltip, ...dialog}"
                            fab small dark
                            class="mr-2"
                            color="blue">
                            <v-icon color="white">
                                mdi-square-edit-outline
                            </v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('base.edit') + ' ' + editable.description[$locale] }}</span>
                </v-tooltip>
            </slot>
        </template>
        <v-card>
            <v-card-title>
                <span class="headline">{{ item ? editable.description[$locale] : label }}</span>
            </v-card-title>

            <v-card-text>
                <v-form
                    ref="editForm"
                    lazy-validation>
                    <translatable-text-field
                        v-model="editable.description"
                        :label="$t('materials.description')">

                    </translatable-text-field>
                    <v-text-field
                        v-model="editable.price"
                        :label="$t('materials.price')"
                        type="number">
                    </v-text-field>
                    <v-select
                        :items="taxes"
                        v-model="editable.tax"
                        :label="$t('materials.tax')"
                        item-text="label"
                        item-value="value">
                    </v-select>
                    <v-text-field
                        v-model="editable.firmao_product_id"
                        :label="$t('materials.firmao_product_id')"
                        type="number">
                    </v-text-field>
                    <v-select
                        v-if="isPlank || isJoist || isSlat"
                        :items="systems"
                        v-model="editable.system_id"
                        :error-messages="errors.system_id"
                        item-text="name"
                        item-value="id"
                        :label="$t('materials.system')">
                    </v-select>
                    <v-select
                        v-if="isAdditional || isComplementary"
                        :items="units"
                        v-model="editable.unit"
                        item-text="label"
                        item-value="id"
                        :label="$t('materials.unit')">
                    </v-select>
                    <v-text-field
                        v-if="isAdditional || isComplementary"
                        :disabled="unit != 5"
                        v-model="editable.package"
                        :label="$t('materials.package')">
                    </v-text-field>
                    <v-select
                        v-if="isAdditional || isComplementary"
                        :items="calculation"
                        v-model="editable.calculation_unit"
                        item-text="label"
                        item-value="id"
                        :label="$t('materials.calculation_unit')">
                    </v-select>
                    <v-text-field
                        v-if="isAdditional || isJoist || isComplementary"
                        v-model="editable.amount_for_unit"
                        :label="$t('materials.amount_for_unit')">
                    </v-text-field>
                    <SlatForm v-if="isSlat" :key="dialog" :item="editable" :errors="errors" @itemChange="(event) => editable.slat=event">

                    </SlatForm>
                    <JoistForm v-if="isJoist" :key="dialog" :item="editable" :errors="errors" @itemChange="(event) => editable.joist=event">

                    </JoistForm>
                    <PlankForm v-if="isPlank" :key="dialog" :item="editable" :errors="errors" @itemChange="(event) => editable.plank=event">

                    </PlankForm>
                    <table v-if="isAdditional && !isComplementary" style="width:80%; margin: 0 auto;">
                        <tr>
                            <td width="50%">
                                <v-subheader>{{ $t('materials.surfaces.notused') }}</v-subheader>
                            </td>
                            <td width="50%">
                                <v-subheader>{{ $t('materials.surfaces.used') }}</v-subheader>
                            </td>
                        </tr>
                        <tr>
                            <td width="50%">
                                <v-btn
                                    v-for="(surface, index) in unusedSurfaces"
                                    :key="index"
                                    outlined block
                                    @click="push(surface)"
                                    color="grey darken-3">{{ surface.name[$locale] }}
                                </v-btn>
                            </td>
                            <td width="50%">
                                <v-btn
                                    v-for="(surface, index) in editable.surfaces"
                                    :key="index"
                                    outlined block
                                    @click="pop(index)"
                                    :color="['indigo darken-3', 'teal darken-3', 'brown darken-4'][index]">{{
                                    surface.name[$locale] }}
                                </v-btn>
                            </td>
                        </tr>
                    </table>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-progress-circular v-if="processing" indeterminate></v-progress-circular>
                <v-btn :disabled="processing" color="blue darken-1" text @click="submit">{{ $t('users.register.submit') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import units from '@/helpers/units'
    import TranslatableTextField from "@/components/common/TranslatableTextField";
    import SlatForm from "@/components/materials/SlatForm";
    import JoistForm from "@/components/materials/JoistForm";
    import PlankForm from "@/components/materials/PlankForm";

    export default {
        name: "MaterialForm",
        components: {TranslatableTextField, SlatForm, JoistForm, PlankForm},
        props: ['item', 'type', 'label'],
        computed: {
            isAdditional() {
                return this.type === 'additional'
            },
            isComplementary() {
                return this.type === 'complementary'
            },
            isJoist() {
                return this.type === 'joist'
            },
            isPlank() {
                return ['plank', 'elevation', 'stair'].includes(this.type)
            },
            isSlat() {
                return ['slat', 'corner'].includes(this.type)
            },
            unit() {
                return this.editable ? this.editable.unit : null
            },
            units() {
                return units.filter(unit => unit.id !== 4)
            },
            calculation() {
                return units.filter(unit => [2, 3, 4].includes(unit.id))
            },
            surfaces() {
                return this.$store.state.materials.surfaces
            },
            systems() {
                return this.$store.state.materials.systems
            },
            unusedSurfaces() {
                return this.surfaces.filter(surface => {
                    return !this.editable.surfaces.find(sur => sur.id === surface.id);
                });
            },
            taxes() {
                return [
                    {
                        label: "23%",
                        value: 23
                    },
                    {
                        label: "8%",
                        value: 8
                    },
                    {
                        label: "0%",
                        value: 0
                    }
                ]
            }
        },
        data() {
            return {
                editable: null,
                dialog: false,
                processing: false,
                errors: {}
            }
        },
        created() {
            this.syncEditable()
        },
        methods: {
            syncEditable() {
                if(this.item) {
                    this.editable = Object.assign({}, this.item)
                } else {
                    this.refresh()
                }
                this.editable.tax = parseInt(this.editable.tax)
                this.errors = {}
            },
            submit() {
                this.processing = true
                this.errors = {}
                let payload = {
                    description: this.editable.description,
                    price: this.editable.price,
                    tax: this.editable.tax,
                    firmao_product_id: this.editable.firmao_product_id,
                    type: this.type,
                    unit: this.editable.unit,
                    slat: this.editable.slat,
                    joist: this.editable.joist,
                    plank: this.editable.plank
                }
                if (this.isPlank || this.isJoist || this.isSlat) {
                    payload = Object.assign(payload, {
                        system_id: this.editable.system_id
                    })
                }
                if (this.isAdditional || this.isComplementary) {
                    payload = Object.assign(payload, {
                        package: this.editable.package,
                        amount_for_unit: this.editable.amount_for_unit,
                        calculation_unit: this.editable.calculation_unit,
                        surfaces: this.editable.surfaces
                    })
                }
                if (this.isJoist) {
                    payload = Object.assign(payload, {
                        amount_for_unit: this.editable.amount_for_unit
                    })
                }
                if(this.item && this.editable.id) {
                    this.submitEdit(payload)
                } else {
                    this.submitCreate(payload)
                }
            },
            submitEdit(payload) {
                this.$http.patch('items/' + this.editable.id, payload)
                    .then(() => {
                        this.$store.commit('updateItem', { payload, id: this.editable.id })
                        this.editable = Object.assign(this.editable, payload)
                        this.processing = false
                        this.dialog = false
                        if (!this.isAdditional) {
                            this.$store.dispatch('prepareMaterials', true)
                        }
                    })
                    .catch((error) => {
                        this.errors = (error.response && error.response.data && error.response.data.errors) || {}
                        this.processing = false
                    })
            },
            submitCreate(payload) {
                this.$http.post('items', payload)
                    .then(({ data }) => {
                        payload.id = data.id
                        this.$store.commit('pushItem', payload)
                        this.refresh()
                        this.processing = false
                        this.dialog = false
                        if (!this.isAdditional) {
                            this.$store.dispatch('prepareMaterials', true)
                        }
                    })
                    .catch((error) => {
                        this.errors = (error.response && error.response.data && error.response.data.errors) || {}
                        this.processing = false
                    })
            },
            push(surface) {
                this.editable.surfaces.push(surface)
            },
            pop(index) {
                this.editable.surfaces.splice(index, 1)
            },
            refresh() {
                this.editable = {
                    amount_for_unit: 1,
                    calculation_unit: 3,
                    description: {pl: '', en: ''},
                    firmao_product_id: null,
                    package: 1,
                    price: null,
                    surfaces: [],
                    system_id: this.systems.length > 0 ? this.systems[0].id : null,
                    tax: 23.00,
                    type: this.type,
                    unit: 1
                }
            }
        },
        watch: {
            unit(val) {
                if(this.dialog && val != 5) {
                    this.editable.package = 1
                }
            },
            dialog(isOpen) {
                if(isOpen) {
                    this.syncEditable()
                }
            }
        }
    }
</script>

<style scoped>

</style>
